/**
 * HC-EMPLOYER-002b — pure calculation functions backing the dashboard's
 * executive stats section. All client-side aggregation over the employer's
 * already-fetched `applications`/`payments` docs — no new Cloud Functions.
 *
 * Every function here returns `null` (not 0, not NaN) when there isn't
 * enough data to compute a meaningful average/rate, so DashboardPage.js can
 * render an explicit "no data yet" empty state instead of a misleading
 * zero. Plain counts/sums (activity trend, spend) return 0 for "no data"
 * instead, since a count or a dollar amount of zero is unambiguous on its
 * own — see the per-function docs below.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Accepts a Firestore Timestamp (has .toMillis()), a JS Date, a millis
// number, or a date string — mirrors the toMillis-style helpers already
// used ad hoc elsewhere in web-admin (e.g. AllApplicantsPage.js's
// formatDate). Returns null for anything unparseable rather than throwing
// or producing NaN.
function toMillis(value) {
  if (value == null) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = new Date(value);
  const t = parsed.getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Average days between application createdAt and hiredAt, across every
 * application that actually has both fields (i.e. was hired).
 * @param {Array<{createdAt: unknown, hiredAt: unknown}>} applications
 * @returns {number|null} null if there are zero hires — never NaN/0 for "no data".
 */
export function computeAverageTimeToHireDays(applications) {
  const hires = (applications || []).filter((a) => a && a.hiredAt && a.createdAt);
  if (hires.length === 0) return null;

  let totalDays = 0;
  let counted = 0;
  hires.forEach((a) => {
    const created = toMillis(a.createdAt);
    const hired = toMillis(a.hiredAt);
    if (created == null || hired == null) return;
    totalDays += Math.max(0, (hired - created) / MS_PER_DAY);
    counted += 1;
  });

  return counted === 0 ? null : totalDays / counted;
}

/**
 * Percentage (0-100) of applications with status 'hired' out of all applications.
 * @param {Array<{status: string}>} applications
 * @returns {number|null} null if there are zero applications at all — distinct
 *   from a real 0% (applicants exist, none converted).
 */
export function computeConversionRate(applications) {
  const total = (applications || []).length;
  if (total === 0) return null;
  const hiredCount = applications.filter((a) => a?.status === 'hired').length;
  return (hiredCount / total) * 100;
}

/**
 * Sum of payment amounts (in cents) that are genuinely completed — status
 * 'succeeded' only. 'pending' (charge not yet confirmed) and 'failed'
 * (charge attempted but did not go through) are excluded — see
 * payments/index.js's createPaymentIntent/confirmHirePayment/stripeWebhook
 * for the full status lifecycle this is based on. Filtering happens inside
 * this function (not left to the caller) so "only succeeded counts" lives
 * in one tested place.
 * @param {Array<{status: string, amount: number}>} payments
 * @returns {number} total cents; 0 when there are no succeeded payments —
 *   an unambiguous "$0 spent", not treated as a null/no-data case.
 */
export function computeTotalSpendCents(payments) {
  return (payments || [])
    .filter((p) => p?.status === 'succeeded')
    .reduce((sum, p) => sum + (Number(p?.amount) || 0), 0);
}

/**
 * Count of applications created within the last `days` days of `now`.
 * @param {Array<{createdAt: unknown}>} applications
 * @param {number} days
 * @param {number} [now] epoch millis, defaults to Date.now()
 * @returns {number} 0 is a valid, unambiguous result here (not treated as "no data").
 */
export function countRecentApplicants(applications, days, now = Date.now()) {
  const cutoff = now - days * MS_PER_DAY;
  return (applications || []).filter((a) => {
    const created = toMillis(a?.createdAt);
    return created != null && created >= cutoff;
  }).length;
}

/**
 * Top N jobs by applicant count, descending. Applications carry their own
 * denormalized jobId/jobTitle (written at application-creation time — see
 * mobile/lib/shared/services/application_service.dart and
 * functions/src/functions/poster-functions.ts), so no separate jobs fetch
 * is needed here.
 * @param {Array<{jobId: string, jobTitle?: string}>} applications
 * @param {number} [limit]
 * @returns {Array<{jobId: string, jobTitle: string, count: number}>} empty
 *   array if there are zero applications with a jobId — caller renders the
 *   "post a job" empty state in that case.
 */
export function computeTopJobs(applications, limit = 3) {
  const counts = new Map();
  (applications || []).forEach((a) => {
    if (!a?.jobId) return;
    const existing = counts.get(a.jobId) || { jobId: a.jobId, jobTitle: a.jobTitle || 'Job', count: 0 };
    existing.count += 1;
    counts.set(a.jobId, existing);
  });
  return [...counts.values()].sort((a, b) => b.count - a.count).slice(0, limit);
}
